import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import storageService from '../utils/storageService';

// Define the language type
type Language = 'en' | 'ja';

// Translations interface
interface Translations {
  [key: string]: {
    en: string;
    ja: string;
  };
}

// Context interface
interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

// Create the context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  toggleLanguage: () => {},
  t: (key: string) => key,
});

// Translations dictionary
const translations: Translations = {
  'home': { en: 'Home', ja: 'ホーム' },
  'docs': { en: 'Docs', ja: 'ドキュメント' },
  'github': { en: 'GitHub', ja: 'GitHub' },
  'products': { en: 'Products', ja: '商品' },
  'search': { en: 'Search', ja: '検索' },
  'account': { en: 'Account', ja: 'アカウント' },
  'cart': { en: 'Cart', ja: 'カート' },
  'imgix_demo': { en: 'Imgix Demo', ja: 'Imgix デモ' },
  'hero_title': { 
    en: 'New image expressions with Generative Fill AI', 
    ja: 'Generative Fill AIで新しい画像表現を' 
  },
  'demo_desc1': { 
    en: 'Powerful image optimization technology from Imgix', 
    ja: 'Imgix のパワフルな画像最適化技術により' 
  },
  'demo_desc2': { 
    en: 'will dramatically change your visual content.', 
    ja: 'あなたのビジュアルコンテンツが劇的に変わります。' 
  },
  'demo_desc3': { 
    en: 'In this demo, you can see how easy it is to achieve automatic image optimization and attractive effects.', 
    ja: 'このデモでは、画像の自動最適化と魅力的なエフェクトがどれほど手軽に実現できるかをご覧いただけます。' 
  },
  'links': { en: 'Links', ja: 'リンク' },
  'company': { en: 'Company', ja: '会社情報' },
  'who_we_are': { en: 'Who we are', ja: '私たちについて' },
  'press': { en: 'Press', ja: 'プレス' },
  'contact': { en: 'Contact', ja: 'お問い合わせ' },
  'get_in_touch': { en: 'Get in touch', ja: 'お問い合わせ' },
  'copyright': { en: '© 2025 Imgix, Inc. All rights reserved.', ja: '© 2025 Imgix, Inc. All rights reserved.' },
  'edit_parameters': { en: 'Edit Parameters', ja: 'パラメータ編集' },
  'close': { en: 'Close', ja: '閉じる' },
  'image_parameters': { en: 'Image Parameters', ja: '画像パラメータ' },
  'instructions': { en: 'Instructions:', ja: '使い方:' },
  'editor_intro': { 
    en: 'Edit the parameters below to see real-time changes to the images. Uncomment lines by removing // to enable those parameters.',
    ja: '下記のパラメータを編集すると、画像にリアルタイムで変更が反映されます。// を削除してパラメータを有効化できます。'
  },
  'uncomment_line': { 
    en: 'Uncomment a line by removing the // at the beginning', 
    ja: '行頭の // を削除してコメント解除' 
  },
  'comment_line': { 
    en: 'Comment out a line by adding // at the beginning', 
    ja: '行頭に // を追加してコメントアウト' 
  },
  'only_uncommented': { 
    en: 'Only uncommented parameters will be applied to images', 
    ja: 'コメントされていないパラメータのみが画像に適用されます' 
  },
  'changes_apply': {
    en: 'Click "Apply Changes" button to update all images',
    ja: '「変更を適用」ボタンをクリックして全ての画像を更新します'
  },
  'language_toggle': { en: 'Japanese', ja: 'English' },
  'product_parameters': { en: 'Product Parameters', ja: '商品パラメータ' },
  'hero_parameters': { en: 'Hero Image Parameters', ja: 'ヒーロー画像パラメータ' },
  'click_parameter_info': { en: 'Click on a parameter name to see detailed information', ja: 'パラメータ名をクリックすると詳細情報が表示されます' },
  'click_add_parameter': { en: 'Click "Add Parameter" to browse and add new parameters', ja: '「Add Parameter」をクリックして新しいパラメータを追加できます' },
  'edit_images': { en: 'Edit Images', ja: '画像編集' },
  'image_urls': { en: 'Image URLs', ja: '画像URL' },
  'image_urls_intro': { en: 'Customize the images displayed on the site by entering your own image URLs.', ja: '独自の画像URLを入力して、サイトに表示される画像をカスタマイズできます。' },
  'hero_image_url': { en: 'Hero Image URL', ja: 'ヒーロー画像URL' },
  'product_image_urls': { en: 'Product Image URLs', ja: '商品画像URL' },
  'one_url_per_line': { en: 'Enter one URL per line. Each URL will replace a product image in order.', ja: 'URLを1行に1つずつ入力してください。各URLは順番に商品画像を置き換えます。' },
  'apply_urls': { en: 'Apply URLs', ja: 'URLを適用' },
  'reset_to_original': { en: 'Reset to Original Images', ja: '元の画像に戻す' },
  'edit_image_sets': { en: 'Edit Image Sets', ja: '画像セット編集' },
  'save_image_set': { en: 'Save Image Set', ja: '画像セットを保存' },
  'image_set_name': { en: 'Image Set Name', ja: '画像セット名' },
  'enter_image_set_name': { en: 'Enter a name for this image set...', ja: 'この画像セットの名前を入力...' },
  'saved_image_sets': { en: 'Saved Image Sets', ja: '保存された画像セット' },
  'no_saved_sets': { en: 'No saved image sets yet', ja: '保存された画像セットはまだありません' },
  'select_image_set': { en: 'Select', ja: '選択' },
  'delete_image_set': { en: 'Delete', ja: '削除' },
  'edit_image_set': { en: 'Edit', ja: '編集' },
  'add_new_set': { en: 'Add New Set', ja: '新規セット追加' },
  'hide_editor': { en: 'Hide Editor', ja: 'エディタを隠す' },
  'update_image_set': { en: 'Update Set', ja: 'セットを更新' },
  'cancel_edit': { en: 'Cancel', ja: 'キャンセル' },
  'export_sets': { en: 'Export Sets', ja: 'セットをエクスポート' },
  'import_sets': { en: 'Import Sets', ja: 'セットをインポート' },
  'export_success': { en: 'Sets exported successfully', ja: 'セットのエクスポートに成功しました' },
  'import_success': { en: 'Sets imported successfully', ja: 'セットのインポートに成功しました' },
  'import_error': { en: 'Error importing sets. Invalid file format.', ja: 'インポート中にエラーが発生しました。無効なファイル形式です。' },
  'file_size_loading': { en: 'Loading...', ja: '読込中...' },
  'file_size_original': { en: 'Original', ja: '元のサイズ' },
  'file_size_optimized': { en: 'Optimized', ja: '最適化' },
  'file_size_saved': { en: 'Saved', ja: '削減率' },
  'file_size': { en: 'Size', ja: 'サイズ' },
  'default': { en: 'Default', ja: 'デフォルト' },
  'selected': { en: 'Selected', ja: '選択済み' },
  'apply_changes': { en: 'Apply Changes', ja: '変更を適用' },
  'add_parameter': { en: 'Add Parameter', ja: 'パラメータを追加' },
  'parameter_browser_title': { en: 'Add Parameter', ja: 'パラメータを追加' },
  'parameter_browser_cancel': { en: 'Cancel', ja: 'キャンセル' },
  'parameter_browser_search': { en: 'Search parameters...', ja: 'パラメータを検索...' },
  'parameter_browser_no_results': { en: 'No parameters found', ja: 'パラメータが見つかりません' },
  'parameter_browser_loading': { en: 'Loading parameters...', ja: 'パラメータを読み込み中...' },
};

// Provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Initialize with saved preference, fallback to user's browser language
  const getBrowserLanguage = (): Language => {
    const browserLang = navigator.language.split('-')[0];
    return browserLang === 'ja' ? 'ja' : 'en';
  };

  // Get language from localStorage, fallback to browser language
  const getInitialLanguage = (): Language => {
    const savedLanguage = storageService.getLanguagePreference();
    return (savedLanguage === 'en' || savedLanguage === 'ja') 
      ? savedLanguage 
      : getBrowserLanguage();
  };

  const [language, setLanguage] = useState<Language>(getInitialLanguage());

  // Update page title when language changes
  useEffect(() => {
    document.title = language === 'en' ? 'Imgix Demo' : 'imgix デモ';
    // Update html lang attribute
    document.documentElement.lang = language;
    // Save language preference to localStorage
    storageService.setLanguagePreference(language);
  }, [language]);

  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'ja' : 'en';
    setLanguage(newLanguage);
    console.log('Language toggled to:', newLanguage);
  };

  // Translation function
  const t = (key: string): string => {
    if (!translations[key]) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    return translations[key][language];
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using the language context
export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;